<script setup lang="ts">
import BatchTypeHeader from '@/components/features/batches/tab-content/BatchTypeHeader.vue'
import Column from 'primevue/column'
import DataTable from 'primevue/datatable'
import StatusTag from '@/components/table/StatusTag.vue'
import MultiCategoryTableFilter from '@/components/table/MultiCategoryTableFilter.vue'
import { RouterLink } from 'vue-router'
import { Icon } from '@iconify/vue'
import { ref, onMounted, computed } from 'vue'
import { BatchState } from '@/types/batch-types'
import { formatedDate } from '@/constants'
import type { Product } from '@/types/product-types'
import { getProductList } from '@/api/products'
import { getFinishedProduct } from '@/constants/productSpecification'
import {getStatusText} from '@/constants/batch'

const props = defineProps<{
  batchList: any
}>()

const products = ref<Product[]>([])
const activeFilters = ref<Record<string, string[]>>({})

const filterGroups = [
  {
    title: 'Quality Control Status',
    key: 'qualityControl',
    options: [
      { label: 'All', value: 'all', id: 'quality-all' },
      { label: 'Pending', value: 'pending', id: 'quality-pending' },
      { label: 'Passed', value: 'passed', id: 'quality-passed' },
      { label: 'Quarantine', value: 'quarantine', id: 'quality-quarantine' },
    ]
  },
  {
    title: 'Fire Testing Status',
    key: 'fireTesting',
    options: [
      { label: 'All', value: 'all', id: 'fire-all' },
      { label: 'Pending', value: 'pending', id: 'fire-pending' },
      { label: 'Passed', value: 'passed', id: 'fire-passed' },
      { label: 'Failed', value: 'failed', id: 'fire-failed' },
    ]
  }
]

const filteredBatchList = computed(() => {
  let filtered = [...props.batchList]

  const qualityFilters = activeFilters.value.qualityControl || []
  if (qualityFilters.length > 0 && !qualityFilters.includes('all')) {
    filtered = filtered.filter((batch: any) => {
      const status = getStatusText(batch.qualityControlStatus, true).toLowerCase()
      return qualityFilters.includes(status)
    })
  }

  const fireFilters = activeFilters.value.fireTesting || []
  if (fireFilters.length > 0 && !fireFilters.includes('all')) {
    filtered = filtered.filter((batch: any) => {
      const status = getStatusText(batch.fireTestStatus, false).toLowerCase()
      return fireFilters.includes(status)
    })
  }

  return filtered
})

const handleFilterChange = (filters: Record<string, string[]>) => {
  activeFilters.value = filters
}

onMounted(async () => {
  products.value = await getProductList()
})
</script>

<template>
  <BatchTypeHeader
      title="Finished"
      description="Find all Finished fabrics here."
      :state="BatchState.FINISHED"
  />

  <div class="flex justify-between items-center mb-4">
    <MultiCategoryTableFilter
        :filter-groups="filterGroups"
        @filter-change="handleFilterChange"
    />
    <div class="text-sm text-gray-600">
      Showing {{ filteredBatchList.length }} of {{ batchList.length }} batches
    </div>
  </div>

  <DataTable :value="filteredBatchList" class="px-0">
    <Column field="batchNumber" header="Batch Number">
      <template #body="slotProps">
        <RouterLink
            :to="`/batches/returned-batch-details/${slotProps.data.id}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ slotProps.data.batchNumber }}
        </RouterLink>
      </template>
    </Column>

    <Column field="finishingQualityId" header="Batch Quality">
      <template #body="slotProps">
        <RouterLink
            :to="`/productSpecification/${slotProps.data.finishingQualityId}`"
            class="font-medium text-neutral-800 block hover:underline"
        >
          {{ getFinishedProduct(slotProps.data.finishingQualityId, products) }}
        </RouterLink>
      </template>
    </Column>

    <Column field="returnedRolls.length" header="Number of rolls">
      <template #body="slotProps">
        {{ slotProps.data.returnedRolls?.length || 0 }}
      </template>
    </Column>

    <Column field="qualityControlStatus" header="Quality Control Status">
      <template #body="slotProps">
        <StatusTag :status="getStatusText(slotProps.data.qualityControlStatus, true)" />
      </template>
    </Column>

    <Column field="fireTestStatus" header="Fire Test Status">
      <template #body="slotProps">
        <StatusTag :status="getStatusText(slotProps.data.fireTestStatus, false)" />
      </template>
    </Column>

    <Column field="dateReturned" header="Date Knitted">
      <template #body="slotProps">
        <span>{{ formatedDate(slotProps.data.dateReturned) }}</span>
      </template>
    </Column>

    <Column field="action">
      <template #body="slotProps">
        <RouterLink
            :to="`/batches/returned-batch-details/${slotProps.data.id}`"
            class="flex items-center gap-x-2 group hover:text-primary-default"
        >
          <span>More Details</span>
          <Icon
              icon="material-symbols:more-up"
              class="text-2xl group-hover:translate-x-2 transition-transform text-primary-500"
          />
        </RouterLink>
      </template>
    </Column>
  </DataTable>
</template>
